import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";

// SS-4391: a patient conversation can be downloaded as one PDF from the Inbox only. Tasks
// render the same thread view at /tasks/:uid, and Team/Intercom/group chat have views of
// their own, so the action is offered on /inbox/:uid and nowhere else.
export const isInboxConversationPath = (pathname) => /^\/inbox\/[^/]+\/?$/.test(pathname || "");

// The api reads and renders the whole history, which takes longer than an ordinary read.
const EXPORT_TIMEOUT_MS = 60000;
const DOWNLOAD_ERROR = "The conversation could not be downloaded. Please try again.";

export const base64ToPdfBlob = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "application/pdf" });
};

const saveBlob = (blob, filename) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

/**
 * Asks whether the PDF should include the internal notes (off by default: the record is
 * often handed to the patient or a lawyer), then downloads it from GET /messages/export.
 */
function DownloadConversationPdfModal({ isOpen, onClose, uid, toast }) {
  const [includeInternalNotes, setIncludeInternalNotes] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // UserInfo mounts one dialog per conversation (key={uid}); a request still running when the
  // user moves on saves its PDF, but must not close or report into the next conversation's dialog
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // every download starts from the safe choice, whatever was ticked last time
  useEffect(() => {
    if (isOpen) setIncludeInternalNotes(false);
  }, [isOpen]);

  const download = async () => {
    setIsDownloading(true);
    try {
      const response = await cqAPI.get(
        "/messages/export",
        { from: uid, include_internal_notes: includeInternalNotes ? "true" : "false" },
        EXPORT_TIMEOUT_MS
      );
      if (response?.type !== "ok" || !response.data?.pdf) {
        if (!mounted.current) return;
        toast({
          title: "Error",
          description: response?.msg || DOWNLOAD_ERROR,
          status: "error",
          duration: 7000,
          isClosable: true,
        });
        return;
      }
      saveBlob(base64ToPdfBlob(response.data.pdf), response.data.filename || "conversation.pdf");
      if (mounted.current) onClose();
    } catch (error) {
      console.error("Failed to download the conversation:", error);
      if (mounted.current) {
        toast({ title: "Error", description: DOWNLOAD_ERROR, status: "error", duration: 7000, isClosable: true });
      }
    } finally {
      if (mounted.current) setIsDownloading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isDownloading ? () => {} : onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Download conversation as PDF</ModalHeader>
        <ModalCloseButton isDisabled={isDownloading} />
        <ModalBody>
          <Text mb={4}>
            The PDF contains every message and call in this conversation, oldest first.
          </Text>
          <Checkbox
            id="download-pdf-include-internal-notes"
            isChecked={includeInternalNotes}
            isDisabled={isDownloading}
            onChange={(e) => setIncludeInternalNotes(e.target.checked)}
          >
            Include internal notes
          </Checkbox>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Internal notes were never sent to the patient.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isDownloading}>
            Cancel
          </Button>
          <Button id="download-pdf-confirm" onClick={download} isLoading={isDownloading} loadingText="Preparing">
            Download
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DownloadConversationPdfModal;
